import { useContext, useEffect, useRef, useState } from "react";

import SearchIcon from "~/icons/SearchIcon";
import ThemeContext from "~/contexts/ThemeContext";
import GoogleContext from "~/contexts/GoogleContext";

import styles from "./AddressPicker.module.css";
import LocationIcon from "~/icons/LocationIcon";
import { PlaceDetails } from "~/helpers/coordsHelpers";

// Small helper type for predictions
type PlacePrediction = google.maps.places.PlacePrediction;

type AddressPickerProps = {
  placeholder?: string;
  autofill?: boolean;
  address: string;
  setAddress: (value: string) => void;
  onPlaceSelect?: (details: PlaceDetails) => void;
  storeLocation?: google.maps.LatLngLiteral;
  radiusMeters?: number;
  /**
   * ISO country codes (e.g. ["NZ"]) the org operates in. When supplied, Places
   * autocomplete is restricted to these countries — this keeps results relevant
   * and cuts Google usage, and works chain-wide instead of inside an arbitrary
   * radius. Takes precedence over the storeLocation/radius circle.
   */
  regionCodes?: string[];
};

const AddressPicker: React.FC<AddressPickerProps> = ({
  placeholder = "Enter your address",
  autofill = false,
  address,
  setAddress,
  onPlaceSelect,
  storeLocation,
  radiusMeters = 10000,
  regionCodes,
}) => {
  const [predictions, setPredictions] = useState<PlacePrediction[]>([]);
  const [isFocused, setIsFocused] = useState(false);
  const [suggestionsError, setSuggestionsError] = useState(false);

  const { template } = useContext(ThemeContext);
  const { isLoaded, loadError } = useContext(GoogleContext);

  const dropdownRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const sessionTokenRef =
    useRef<google.maps.places.AutocompleteSessionToken | null>(null);
  // Increments on each fetch so a slower earlier response can't overwrite a
  // newer one (clearTimeout only cancels the debounce, not an in-flight fetch).
  const fetchSeqRef = useRef(0);

  useEffect(() => {
    if (!isLoaded || !window.google?.maps?.importLibrary) return;

    (async () => {
      const placesLib = (await window.google.maps.importLibrary(
        "places",
      )) as google.maps.PlacesLibrary;

      if (!sessionTokenRef.current) {
        sessionTokenRef.current = new placesLib.AutocompleteSessionToken();
      }
    })();
  }, [isLoaded]);

  // Autofill current location
  useEffect(() => {
    if (!autofill || !isLoaded || !window.google?.maps?.Geocoder) {
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        const { latitude, longitude } = position.coords;

        const geocoder = new window.google.maps.Geocoder();
        geocoder.geocode(
          { location: { lat: latitude, lng: longitude } },
          (results, status) => {
            if (status === "OK" && results?.[0]?.formatted_address) {
              setAddress(results[0].formatted_address);
              setPredictions([]);
            } else {
              console.error("Failed to get address by coordinates");
            }
          },
        );
      },
      (error) => {
        // Permission denial / timeout / unavailable — degrade gracefully,
        // the user can still type their address manually.
        console.warn("Geolocation autofill unavailable:", error.message);
      },
    );
  }, [address, autofill, isLoaded, setAddress]);

  // regionCodes is often an array literal: a stable string key stops a new identity with the same values from
  // refiring the fetch
  const regionKey = (regionCodes ?? []).join(",");

  useEffect(() => {
    if (!isLoaded || !window.google?.maps?.importLibrary) return;

    // If empty input, close dropdown immediately (no debounce needed)
    if (!address.trim()) {
      setPredictions([]);
      setSuggestionsError(false);
      return;
    }

    // Debounce the Places fetch ~150ms to avoid hammering the API on every keystroke
    const timer = setTimeout(async () => {
      // Sequence guard: only the newest request may apply its result, so a
      // slower earlier fetch can't clobber a newer one after it resolves.
      const requestId = ++fetchSeqRef.current;

      try {
        const placesLib = (await window.google.maps.importLibrary(
          "places",
        )) as google.maps.PlacesLibrary;

        // Ensure token exists
        if (!sessionTokenRef.current) {
          sessionTokenRef.current = new placesLib.AutocompleteSessionToken();
        }

        const normalizedRegions = regionKey
          .split(",")
          .map((c) => c.trim().toLowerCase())
          .filter(Boolean);

        const autocompleteRequest: google.maps.places.AutocompleteRequest = {
          input: address,
          sessionToken: sessionTokenRef.current,
          origin: storeLocation,
        };

        if (normalizedRegions.length) {
          // Country gate: relevant results chain-wide, far less Google noise.
          // origin (above) still biases ordering toward the nearest store.
          autocompleteRequest.includedRegionCodes = normalizedRegions;
        } else if (storeLocation) {
          // No country known — fall back to a hard circle around the stores.
          const boundsLiteral = new window.google.maps.Circle({
            center: storeLocation,
            radius: radiusMeters,
          })
            .getBounds()
            ?.toJSON();
          if (boundsLiteral) {
            autocompleteRequest.locationRestriction = boundsLiteral;
          }
        }

        // Fetch autocomplete suggestions
        const { suggestions } =
          await placesLib.AutocompleteSuggestion.fetchAutocompleteSuggestions(
            autocompleteRequest,
          );

        // A newer request superseded this one — drop the stale result.
        if (requestId !== fetchSeqRef.current) return;

        // Take only place predictions
        const placePredictions = suggestions
          .map((s) => s.placePrediction)
          .filter((p): p is PlacePrediction => Boolean(p));

        setSuggestionsError(false);
        setPredictions(placePredictions);
      } catch (error) {
        // Ignore stale failures; only surface the newest request's error.
        if (requestId !== fetchSeqRef.current) return;
        console.error("Failed to fetch address suggestions", error);
        setPredictions([]);
        setSuggestionsError(true);
      }
    }, 150);

    return () => clearTimeout(timer);
  }, [address, isLoaded, radiusMeters, storeLocation, regionKey]);

  // Selecting a prediction -> fetch place details via Place.fetchFields()
  const handleSelect = async (p: PlacePrediction) => {
    if (!isLoaded || !window.google?.maps?.importLibrary) return;

    const placesLib = (await window.google.maps.importLibrary(
      "places",
    )) as google.maps.PlacesLibrary;

    const place = p.toPlace();

    await place.fetchFields({
      fields: ["formattedAddress", "location", "addressComponents"],
    });

    if (place.formattedAddress) {
      setAddress(place.formattedAddress);
    }

    if (onPlaceSelect && place.formattedAddress && place.location) {
      onPlaceSelect({
        formattedAddress: place.formattedAddress,
        latitude: place.location.lat(),
        longitude: place.location.lng(),
        addressComponents: place.addressComponents ?? [],
      });
    }

    sessionTokenRef.current = new placesLib.AutocompleteSessionToken();

    inputRef.current?.blur();
  };

  // Styles
  const inputStyles = {
    backgroundColor: template.global.input.backgroundColour,
    borderColor: template.global.input.borderColour,
    color: template.global.input.fontColour,
    "--focus-search-select-background-colour":
      template.global.input.focusBackgroundColour,
    "--focus-search-select-border-colour":
      template.global.input.focusBorderColour,
  };

  // Autocomplete is only layered on when Maps loaded cleanly. If the script
  // failed to load we still render a plain input so the user can type and
  // submit a free-text address — delivery checkout must never be blocked.
  const suggestionsAvailable = isLoaded && !loadError;

  return (
    <div className={styles.wrapper}>
      <div className={styles.searchIconWrapper}>
        <SearchIcon />
      </div>

      <input
        type="text"
        placeholder={placeholder}
        ref={inputRef}
        className={styles.input}
        style={inputStyles as React.CSSProperties}
        value={address}
        onChange={(e) => setAddress(e.target.value)}
        onFocus={() => setIsFocused(true)}
        onBlur={() => setIsFocused(false)}
        autoComplete="off"
      />

      {suggestionsAvailable && isFocused && predictions.length > 0 && (
        <div ref={dropdownRef} className={styles.dropdown}>
          {predictions.map((p) => (
            <button
              key={p.placeId}
              type="button"
              className={styles.dropdownItem}
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => handleSelect(p)}
            >
              <LocationIcon />
              {p.text?.text ?? ""}
            </button>
          ))}
        </div>
      )}

      {suggestionsAvailable &&
        isFocused &&
        !suggestionsError &&
        predictions.length === 0 &&
        address.trim().length > 0 && (
          <div ref={dropdownRef} className={styles.dropdown}>
            <div className={styles.dropdownEmpty}>No matching addresses</div>
          </div>
        )}

      {!suggestionsAvailable && (
        <p className={styles.hint}>
          Address suggestions are unavailable — please type your full address.
        </p>
      )}

      {suggestionsAvailable && suggestionsError && (
        <p className={styles.hint}>
          Couldn&apos;t load suggestions — you can type your address.
        </p>
      )}
    </div>
  );
};

export default AddressPicker;
